import React, {FC, useEffect} from "react";
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Typography
} from "@mui/material";
import {LoadingButton} from "../../../../../../newShared/components/Basic/CommonButtons";
import {useMainTranslation} from "../../../../../../newShared/hooks/useMainTranslationHooks/useMainTranslation";
import {
    useRequiredActionsPerformProvideFilesDialog
} from "../../../hooks/dialogs/useRequiredActionsPerformProvideFilesDialog";
import {
    FlexColumn,
    FlexRow
} from "../../../../../../newShared/components/editorUnion/components/editorTitleWithActionsRow/styled";
import colors from "../../../../../../newShared/theme/colors";
import {DeleteOutline} from "@mui/icons-material";
import {FileDrop} from "react-file-drop";
import {FileDropZone} from "../../../../../../newShared/components/fileDropZone";
import {useMultiFileDropZone} from "../../../../../../newShared/hooks/useMultiFileDropZone";
import {useMessageDialog} from "../../../../../barsEnvironment/MessageDialog/hooks/useMessageDialog";
import {ACCEPT_IMAGE_PROP} from "../../../../../../newShared/constants";

export const RequiredActionsPerformProvideFilesDialog: FC<{refreshTable: () => void}> = ({refreshTable}) => {
    const {t, tCommon} = useMainTranslation('', {keyPrefix: 'pathRequiredActions.dialogs'});

    const {
        isOpen,
        isLoading,
        handleClose,
        formik, handleDeleteFile, handleFileUpload,
    } = useRequiredActionsPerformProvideFilesDialog({refreshTable});

    const limitMB = 50;
    const limitCount = 25;

    const {fileHandler, filesInBase64, ref, filePicker, reset} = useMultiFileDropZone(limitMB);
    const {isOpen: isOpenMessageDialog} = useMessageDialog();

    useEffect(() => {
        filesInBase64.length && handleFileUpload(filesInBase64.map(e => ({base64: e.file, fileName: e.name})));
        reset();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(filesInBase64)]);

    return (
        <Dialog open={isOpen && !isOpenMessageDialog} onClose={handleClose}>
            <DialogTitle>{t('File attachment')}</DialogTitle>

            <DialogContent sx={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
                <Typography variant={'*bodyText2'} color={formik.touched.attachmentsOrComment && formik.errors.attachmentsOrComment ? colors.text.critical : undefined}>
                    {t('Required to provide files or comment.')}
                </Typography>

                <FlexColumn width={'100%'} gap={'4px'}>
                    <Typography variant={'*bodyText2_semibold'}>{t('Attachment')}</Typography>
                    <Typography variant={'*bodyText2'} color={colors.text.grey} sx={{mt: '4px'}}>
                        {tCommon('Make sure that the attached file does not exceed {{mb}} MB.', {mb: limitMB})}
                    </Typography>

                    {formik.values.attachments.length > limitCount && (
                        <Typography variant={'*bodyText2'} color={colors.text.critical}>
                            {tCommon('You can upload up to {{count}} files', {count: limitCount})}
                        </Typography>
                    )}
                    {formik.values.attachments.map(e => (
                        <FlexRow sx={{alignItems: 'center', width: '100%', overflow: 'hidden', gap: '8px'}} key={e.fileId}>
                            <Typography variant={'*bodyText2'} sx={{flexGrow: 1}} noWrap>
                                {e.fileName}
                            </Typography>

                            {e.fileId.startsWith('uploading') ? (
                                <IconButton size={"small"} sx={{flexShrink: 0}} disabled>
                                    <CircularProgress size={24}/>
                                </IconButton>
                            ) : (
                                <IconButton size={"small"} sx={{flexShrink: 0}} onClick={handleDeleteFile(e.fileId)} color={"primary"}>
                                    <DeleteOutline/>
                                </IconButton>
                            )}
                        </FlexRow>
                    ))}
                </FlexColumn>

                {formik.values.attachments.length < limitCount && (
                    <FileDrop onTargetClick={filePicker} onDrop={fileHandler}>
                        <FileDropZone inputRef={ref}
                                      fileHandler={fileHandler}
                                      accept={`.pdf, .xlsx, .docx, .xls, .doc, .zip, ${ACCEPT_IMAGE_PROP}`}
                                      multiple
                        />
                    </FileDrop>
                )}

                <TextField rows={4} multiline
                           error={formik.touched.comment && Boolean(formik.errors.comment)}
                           helperText={formik.touched.comment && formik.errors.comment}
                           onChange={formik.handleChange}
                           onBlur={formik.handleBlur}
                           value={formik.values.comment}
                           name={'comment'}
                           label={t('Comment')}
                />
            </DialogContent>

            <DialogActions>
                <Button variant={"text"} onClick={handleClose}>{tCommon('Cancel')}</Button>
                <LoadingButton size={'small'} variant={"contained"}
                               onClick={formik.submitForm}
                               disabled={!formik.isValid || formik.values.attachments.some(e => e.fileId.startsWith('uploading'))}
                               loading={isLoading.performProvideFiles}
                >{t('Done')}</LoadingButton>
            </DialogActions>

        </Dialog>
    )
}